import { Injectable } from '@angular/core';
import { GooglePlacePickerModel } from '../_models/google-place-picker.model';
import { AddressComponent } from '@data-access';

const CACHE_KEY_COORDINATE_PRECISION = 4;

@Injectable({ providedIn: 'root' })
export class GeocodingService {
  private readonly componentsCache = new Map<string, AddressComponent[]>();

  /** Reverse-geocode a coordinate into address step-components matching the app's
   *  AddressComponent model; cached by coordinate, empty array if nothing matched. */
  async reverseGeocodeComponents(location: google.maps.LatLngLiteral): Promise<AddressComponent[]> {
    const key = `${location.lat.toFixed(CACHE_KEY_COORDINATE_PRECISION)},${location.lng.toFixed(CACHE_KEY_COORDINATE_PRECISION)}`;
    const cached = this.componentsCache.get(key);
    if (cached) return cached;

    const result = await this.geocodeFirst(location);
    const components = convertAddressComponentsToArray(result?.address_components);

    // A miss is often a failed or rate-limited request, so the next caller asks again.
    if (components.length) this.componentsCache.set(key, components);
    return components;
  }

  /** Reverse-geocode a coordinate into a place; `undefined` if nothing matched. */
  async reverseGeocode(
    location: google.maps.LatLngLiteral,
  ): Promise<GooglePlacePickerModel | undefined> {
    const first = await this.geocodeFirst(location);
    return first ? this.parsePlace(first) : undefined;
  }

  /**
   * First raw geocoder result for a coordinate; `undefined` on miss/failure.
   * Google ranks results from most specific (plus code, POI, street) down to broadest
   * (country), and the most specific one doesn't always carry a `postal_code` component -
   * e.g. plus codes and points of interest routinely omit it even though a ZIP always exists
   * for the underlying area. Prefer the top result when it already has one; otherwise fall
   * back to the first result in the list that does, since Google returns a dedicated
   * `postal_code`-typed result for essentially any US/CA coordinate.
   */
  private async geocodeFirst(
    location: google.maps.LatLngLiteral,
  ): Promise<google.maps.GeocoderResult | undefined> {
    await google.maps.importLibrary('geocoding');
    const geocoder = new google.maps.Geocoder();

    try {
      const { results } = await geocoder.geocode({ location });
      if (!results?.length) return undefined;

      const hasPostalCode = (result: google.maps.GeocoderResult) =>
        result.address_components.some((component) => component.types.includes('postal_code'));

      const first = results[0]!;
      return hasPostalCode(first) ? first : (results.find(hasPostalCode) ?? first);
    } catch {
      return undefined;
    }
  }

  /** Map a Google geocoder/autocomplete result into our flat place model. */
  parsePlace(
    result: google.maps.GeocoderResult | google.maps.places.PlaceResult,
  ): GooglePlacePickerModel | undefined {
    const location = result.geometry?.location;
    if (!location) return undefined;

    const place: GooglePlacePickerModel = {
      lat: location.lat(),
      lng: location.lng(),
      formattedAddress: result.formatted_address ?? '',
    };

    for (const component of result.address_components ?? []) {
      const types = component.types;
      if (types.includes('postal_code')) place.postalCode = component.long_name.replaceAll(' ', '');
      if (types.includes('country')) place.countryCode = component.short_name;
    }

    return {
      ...place,
      placeMetadata: JSON.stringify({ address_components: result.address_components ?? [] }),
      addressComponents: convertAddressComponentsToArray(result.address_components),
    };
  }
}

/** Google geocoder step-components => our AddressComponent[] */
export function convertAddressComponentsToArray(
  addressComponents?: google.maps.GeocoderAddressComponent[],
): AddressComponent[] {
  return (
    addressComponents?.map((cmp) => ({
      longName: cmp.long_name,
      shortName: cmp.short_name,
      types: cmp.types,
    })) ?? []
  );
}

/** AddressComponent[] => JSON string for placeMetadata */
export function convertAddressComponentsToJSONMetadata(
  addressComponents?: AddressComponent[],
): string {
  const converted: google.maps.GeocoderAddressComponent[] =
    addressComponents?.map((cmp: AddressComponent): google.maps.GeocoderAddressComponent => ({
      long_name: cmp.longName ?? '',
      short_name: cmp.shortName ?? '',
      types: cmp.types ?? [],
    })) ?? [];

  return JSON.stringify({
    address_components: converted,
  });
}
